/* @jsx h */
import { defineComponent, h, PropType, computed, ref, watch } from "vue";
import type {
  ReservationSchedule,
  TimeSlotAvailability,
} from "@lemmonhq/types";
import { fetchReservationAvailability } from "@app/server/client";
import { daysOfWeek, getTodayInTimezone } from "@lemmonhq/orders";
import { useLocalization } from "@lemmonhq/uikit";

export type ServicePeriod = {
  id: string;
  name: string;
  icon: string;
  startHour: number;
  endHour: number;
};

// Default service periods
const WEEKDAY_SERVICES: ServicePeriod[] = [
  {
    id: "breakfast",
    name: "Breakfast",
    icon: "coffee",
    startHour: 7,
    endHour: 11,
  },
  {
    id: "lunch",
    name: "Lunch",
    icon: "lunch_dining",
    startHour: 11,
    endHour: 17,
  },
  {
    id: "dinner",
    name: "Dinner",
    icon: "dinner_dining",
    startHour: 17,
    endHour: 24,
  },
  {
    id: "all_night",
    name: "All Night",
    icon: "nightlight",
    startHour: 0,
    endHour: 7,
  },
];

const SUNDAY_SERVICES: ServicePeriod[] = [
  {
    id: "breakfast",
    name: "Breakfast",
    icon: "coffee",
    startHour: 7,
    endHour: 11,
  },
  {
    id: "lunch",
    name: "Lunch",
    icon: "lunch_dining",
    startHour: 11,
    endHour: 17,
  },
  {
    id: "dinner",
    name: "Dinner",
    icon: "dinner_dining",
    startHour: 17,
    endHour: 24,
  },
  {
    id: "all_night",
    name: "All Night",
    icon: "nightlight",
    startHour: 0,
    endHour: 7,
  },
];

export const ServiceTimeSelector = defineComponent({
  name: "ServiceTimeSelector",
  props: {
    selectedDate: {
      type: Date as PropType<Date>,
      required: true,
    },
    selectedService: {
      type: String as PropType<string | null>,
      default: null,
    },
    selectedTime: {
      type: String as PropType<string>,
      default: "",
    },
    schedule: {
      type: Object as PropType<ReservationSchedule>,
      default: () => ({}),
    },
    targetPartySize: {
      type: Number,
      default: undefined,
    },
    slotMinutes: {
      type: Number,
      default: 15,
    },
    compact: {
      type: Boolean,
      default: false,
    },
    shopSlug: {
      type: String,
      required: true,
    },
    timezone: {
      type: String,
      default: "UTC",
    },
  },
  emits: ["update:selectedService", "update:selectedTime"],
  setup(props, { emit }) {
    const { t } = useLocalization();
    const availabilityData = ref<TimeSlotAvailability[]>([]);
    const availabilityDate = ref<string | null>(null);
    const loadingAvailability = ref(false);
    const initialLoadComplete = ref(false);
    const availabilityError = ref<string | null>(null);
    // Why the day has no slots (paused / vacation / closed) and the
    // restaurant-authored note shown to guests for that closure.
    const closedReason = ref<string | undefined>(undefined);
    const closedGuestNote = ref<string | undefined>(undefined);
    const isSunday = computed(() => props.selectedDate.getDay() === 0);

    // Helper to format date as YYYY-MM-DD in local timezone
    const formatDateLocal = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    // Get the day name for the selected date
    const getDayName = () => {
      const days = daysOfWeek;
      return days[props.selectedDate.getDay()];
    };

    // Generate all possible time slots for the day based on schedule
    // Generates slots starting from the beginning of each hour within schedule ranges
    const generateAllTimeSlots = (): string[] => {
      const dayName = getDayName();
      const daySchedule = props.schedule[dayName];
      if (!daySchedule || daySchedule.length === 0) return [];

      const slots: string[] = [];
      const slotSet = new Set<string>(); // Use Set to avoid duplicates

      for (const range of daySchedule) {
        const [startHour, startMin] = range.start_time.split(":").map(Number);
        const [endHour, endMin] = range.end_time.split(":").map(Number);

        const startMinutes = startHour * 60 + startMin;
        const endMinutes = endHour * 60 + endMin;

        // Round up to next slot interval
        const firstSlotMinute =
          Math.ceil(startMinutes / props.slotMinutes) * props.slotMinutes;

        for (
          let mins = firstSlotMinute;
          mins < endMinutes;
          mins += props.slotMinutes
        ) {
          const hour = Math.floor(mins / 60);
          const minute = mins % 60;
          const timeStr = `${hour.toString().padStart(2, "0")}:${minute
            .toString()
            .padStart(2, "0")}`;
          slotSet.add(timeStr);
        }
      }

      return Array.from(slotSet).sort();
    };

    // Load availability data from API
    const loadAvailability = async () => {
      if (!props.selectedDate || !props.shopSlug) return;

      const dateStr = formatDateLocal(props.selectedDate);
      const slotsToCheck = generateAllTimeSlots();

      loadingAvailability.value = true;
      availabilityError.value = null;
      // Clear old data to prevent showing stale availability
      availabilityData.value = [];
      availabilityDate.value = null;
      closedReason.value = undefined;
      closedGuestNote.value = undefined;

      try {
        const result = await fetchReservationAvailability(
          props.shopSlug,
          dateStr,
          props.targetPartySize,
          slotsToCheck,
        );
        availabilityData.value = result.slots || [];
        availabilityDate.value = result.date || null;
        closedReason.value = result.closedReason;
        closedGuestNote.value = result.closedGuestNote;
      } catch (err) {
        console.error(
          "[ServiceTimeSelector] Failed to fetch availability:",
          err,
        );
        availabilityError.value =
          err instanceof Error
            ? err.message
            : t("customer.reserve.availability_load_failed");
        availabilityData.value = [];
      } finally {
        loadingAvailability.value = false;
        initialLoadComplete.value = true;
      }
    };

    const retryLoadAvailability = () => {
      availabilityError.value = null;
      initialLoadComplete.value = false;
      loadAvailability();
    };

    // Watch for date changes to reload availability
    watch(
      () => formatDateLocal(props.selectedDate),
      () => {
        // Reset service selection when date changes
        //emit("update:selectedService", null);
        //emit("update:selectedTime", "");
        loadAvailability();
      },
      { immediate: true },
    );

    watch(
      () => props.targetPartySize,
      () => {
        loadAvailability();
      },
    );

    // Helper to check if a time slot is available
    const isTimeSlotAvailable = (time: string): boolean => {
      // If we haven't loaded availability yet, show all as unavailable
      if (!initialLoadComplete.value) return false;

      // If no availability data, assume all slots are available
      if (availabilityData.value.length === 0) return true;

      const slot = availabilityData.value.find((s) => s.time === time);
      if (!slot) {
        return false;
      }
      return slot.available;
    };

    // Helper to get unavailability reason with user-friendly message
    const getUnavailabilityReason = (time: string): string | undefined => {
      const slot = availabilityData.value.find((s) => s.time === time);
      if (!slot || slot.available) return undefined;

      // Map reason codes to user-friendly messages
      switch (slot.reason) {
        case "past_time":
          return t("customer.reserve.reason_past_time");
        case "fully_booked":
          return t("customer.reserve.reason_fully_booked");
        case "outside_hours":
          return t("customer.reserve.reason_outside_hours");
        case "closed":
          return t("customer.reserve.reason_closed");
        case "no_capacity":
          return t("customer.reserve.reason_no_capacity");
        default:
          return slot.reason || t("customer.reserve.reason_not_available");
      }
    };

    // Helper to get remaining capacity for a time slot
    const getRemainingCapacity = (time: string): number | undefined => {
      const slot = availabilityData.value.find((s) => s.time === time);
      return slot?.remainingCapacity;
    };

    // Get available services based on schedule and day
    const availableServices = computed(() => {
      const dayName = getDayName();
      const daySchedule = props.schedule[dayName];

      if (!daySchedule || daySchedule.length === 0) {
        return [];
      }

      // Filter standard services that overlap with the schedule
      const baseServices = isSunday.value ? SUNDAY_SERVICES : WEEKDAY_SERVICES;

      const overlappingServices = baseServices.filter((service) => {
        return daySchedule.some((range) => {
          const [startHour] = range.start_time.split(":").map(Number);
          const [endHour] = range.end_time.split(":").map(Number);
          // Service overlaps if its time range intersects with schedule
          return service.startHour < endHour && service.endHour > startHour;
        });
      });

      return overlappingServices;
    });

    // Get time slots for the selected service based on service period hours
    const timeSlots = computed(() => {
      if (!props.selectedService) return [];
      if (!initialLoadComplete.value || availabilityData.value.length === 0)
        return [];

      // Find the selected service from available services
      const service = availableServices.value.find(
        (s) => s.id === props.selectedService,
      );
      if (!service) return [];

      // Get the schedule for the day
      const dayName = getDayName();
      const daySchedule = props.schedule[dayName];
      if (!daySchedule || daySchedule.length === 0) return [];

      // Filter availability data by:
      // 1. Service period hours (e.g., Dinner is 17-24)
      // 2. Restaurant schedule (e.g., actual operating hours)
      // 3. Exclude slots with reason "past_time"
      return availabilityData.value
        .filter((slot) => {
          const [hour, minute] = slot.time.split(":").map(Number);
          const slotMinutes = hour * 60 + minute;

          // Check if slot is within service period
          const inServicePeriod =
            hour >= service.startHour && hour < service.endHour;
          if (!inServicePeriod) return false;

          // Check if slot intersects with any restaurant schedule range
          const inSchedule = daySchedule.some((range) => {
            const [startHour, startMin] = range.start_time
              .split(":")
              .map(Number);
            const [endHour, endMin] = range.end_time.split(":").map(Number);
            const startMinutes = startHour * 60 + startMin;
            const endMinutes = endHour * 60 + endMin;

            return slotMinutes >= startMinutes && slotMinutes < endMinutes;
          });

          if (!inSchedule) return false;

          // Exclude slots that are in the past or blocked by a staff-set
          // closure range ("closed") — those are never offered to guests.
          if (
            !slot.available &&
            (slot.reason === "past_time" || slot.reason === "closed")
          ) {
            return false;
          }

          return true;
        })
        .map((slot) => slot.time);
    });

    // Auto-select service period
    watch(
      availableServices,
      (services) => {
        if (services.length === 0 || props.selectedService) return;

        // Get current time in restaurant's timezone
        const nowInRestaurantTz = getTodayInTimezone(props.timezone);
        const todayInRestaurantTz = getTodayInTimezone(props.timezone);

        // Check if selected date is today in restaurant's timezone
        const isToday =
          props.selectedDate.toDateString() ===
          todayInRestaurantTz.toDateString();

        if (isToday) {
          // For today, select the current active service period
          const currentHour = nowInRestaurantTz.getHours();
          const activeService = services.find(
            (s) => currentHour >= s.startHour && currentHour < s.endHour,
          );
          if (activeService) {
            emit("update:selectedService", activeService.id);
          } else {
            // If no active service, select the first one
            emit("update:selectedService", services[0].id);
          }
        } else {
          // For future dates, select the first service period
          emit("update:selectedService", services[0].id);
        }
      },
      { immediate: true },
    );

    const selectService = (serviceId: string) => {
      emit("update:selectedService", serviceId);
      emit("update:selectedTime", ""); // Reset time when service changes
    };

    const selectTime = (time: string) => {
      emit("update:selectedTime", time);
    };

    const formatDate = (date: Date) => {
      return date.toLocaleDateString("en-US", {
        weekday: "long",
        month: "long",
        day: "numeric",
      });
    };

    return () => (
      <div
        class={
          props.compact
            ? "space-y-4"
            : "bg-white rounded-lg border border-gray-200 p-4 space-y-4"
        }
      >
        <div class="flex items-center justify-between">
          <h3 class="text-base font-semibold text-gray-900">
            {t("customer.reserve.select_time")}
          </h3>
          <div class="flex items-center gap-2">
            {loadingAvailability.value && (
              <span class="loading loading-spinner loading-xs"></span>
            )}
            {availabilityDate.value && (
              <span class="text-xs text-gray-400">
                {availabilityDate.value}
              </span>
            )}
          </div>
        </div>

        {/* Show error state */}
        {availabilityError.value ? (
          <div class="flex flex-col items-center justify-center py-12 text-gray-500">
            <span class="material-symbols-outlined text-5xl mb-3 text-error">
              error
            </span>
            <p class="text-sm font-medium text-error mb-2">
              {t("customer.reserve.availability_load_failed_title")}
            </p>
            <p class="text-xs text-gray-500 mb-4 max-w-xs text-center">
              {availabilityError.value}
            </p>
            <button
              type="button"
              class="btn btn-primary btn-sm"
              onClick={retryLoadAvailability}
            >
              <span class="material-symbols-outlined text-lg">refresh</span>
              {t("customer.reserve.try_again")}
            </button>
          </div>
        ) : !initialLoadComplete.value ? (
          <div class="flex flex-col items-center justify-center py-12 text-gray-500">
            <span class="loading loading-spinner loading-lg mb-3"></span>
            <p class="text-sm font-light">
              {t("customer.reserve.loading_availability")}
            </p>
          </div>
        ) : availableServices.value.length === 0 ? (
          <div class="text-center py-6 text-gray-500">
            <span class="material-symbols-outlined text-4xl mb-2 block">
              event_busy
            </span>
            <p class="font-light">
              {t("customer.reserve.no_reservations_for_date")}
            </p>
          </div>
        ) : (
          <>
            {/* Service Period Selection */}
            <div class="flex flex-wrap gap-2">
              {availableServices.value.map((service) => (
                <button
                  key={service.id}
                  type="button"
                  data-testid="service-period-btn"
                  onClick={() => selectService(service.id)}
                  class={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm border transition-all ${
                    props.selectedService === service.id
                      ? "bg-gray-900 text-white border-gray-900"
                      : "bg-white border-gray-200 hover:border-gray-300 hover:bg-gray-50 text-gray-700"
                  }`}
                >
                  <span class="material-symbols-outlined text-base">
                    {service.icon}
                  </span>
                  <span>{t(`customer.reserve.service_${service.id}`)}</span>
                </button>
              ))}
            </div>

            {/* Time Slots */}
            {props.selectedService && (
              <div class="space-y-2">
                <h4 class="text-sm font-medium text-gray-700">
                  {t("customer.reserve.available_times")}
                </h4>
                {loadingAvailability.value ? (
                  <div class="flex items-center justify-center py-8">
                    <span class="loading loading-spinner loading-md"></span>
                  </div>
                ) : timeSlots.value.length === 0 ? (
                  <div class="text-center py-4 space-y-1">
                    <p class="text-sm text-gray-500">
                      {closedReason.value === "paused"
                        ? t("customer.reserve.busy_today")
                        : closedReason.value
                          ? t("customer.reserve.closed_today")
                          : t("customer.reserve.no_slots_available")}
                    </p>
                    {closedReason.value && (
                      <p class="text-xs text-gray-400">
                        {closedReason.value === "paused"
                          ? t("customer.reserve.try_another_date")
                          : t("customer.reserve.select_another_date")}
                      </p>
                    )}
                    {closedGuestNote.value && (
                      <p class="text-sm text-gray-700 pt-1">
                        {closedGuestNote.value}
                      </p>
                    )}
                  </div>
                ) : (
                  <div class="grid grid-cols-4 gap-2">
                    {timeSlots.value.map((time) => {
                      const available = isTimeSlotAvailable(time);
                      const reason = !available
                        ? getUnavailabilityReason(time)
                        : undefined;
                      const remainingCapacity = getRemainingCapacity(time);

                      // Build tooltip text
                      let tooltipText: string | undefined;
                      if (reason) {
                        tooltipText = reason;
                      } else if (remainingCapacity !== undefined) {
                        tooltipText = t(
                          remainingCapacity === 1
                            ? "customer.reserve.remaining_spot"
                            : "customer.reserve.remaining_spots",
                          [remainingCapacity],
                        );
                        if (import.meta.env.VITE_ENV === "production") {
                          tooltipText = t("customer.reserve.free");
                        }
                      }

                      return (
                        <div
                          key={time}
                          class={tooltipText ? "tooltip" : ""}
                          data-tip={tooltipText}
                        >
                          <button
                            type="button"
                            onClick={() =>
                              available &&
                              !loadingAvailability.value &&
                              selectTime(time)
                            }
                            data-time={time}
                            data-testid="time-slot"
                            disabled={!available || loadingAvailability.value}
                            class={`w-full px-2 py-2 text-base rounded-lg border transition-all font-light ${
                              loadingAvailability.value
                                ? "pointer-events-none opacity-50"
                                : !available
                                  ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed opacity-50"
                                  : props.selectedTime === time
                                    ? "bg-gray-900 text-white border-gray-900"
                                    : "bg-white border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                            }`}
                          >
                            {time}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    );
  },
});
